import React, { useCallback, useContext, useMemo } from "react";
import { useStyles } from "../../RCACompliance/RCACompliance.styles";
import { IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ComplianceBar from "../../components/ComplianceBar";
import { RCAAPComplianceContext } from "contexts/RCAAPComplianceContext/RCAAPComplianceContext";
import moment from "moment";
import { RCAAPComplianceEntity } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntity";
import { ComplianceBarReferences } from "../../components/ComplianceBar/ComplianceBarReferences.view";
import { ComplianceBarPercentageReference } from "../../components/ComplianceBar/ComplianceBarPercentageReference.view";
import RCAAPComplianceByResponsibleFilter from "../../components/filters/RCAAPComplianceByResponsibleFilter/RCAAPComplianceByResponsibleFilter.view";
import { IAPComplianceByResponsible } from "entities/RCA/RCAAPComplianceEntity/RCAAPComplianceEntityInterfaces";
import { IRCAAPBarComplianceTypes, RCAAPComplianceByResponsibleFilterEnum } from "../../RCAAPComplianceInterfaces";
import { getComplianceByType, getRCAAPComplianceScoreByResponsible } from "utilities/utilities";
import { ComplianceTypes } from "constants/constants";
import RefreshIcon from "@mui/icons-material/Refresh";

const APComplianceByResponsible = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters, handleSetTypeFilter, handleSetComplianceType } = useContext(RCAAPComplianceContext);
    const argsFilters = useMemo(
        () => ({
            date: moment().subtract(Number(filters.periodOption), "day").startOf("day").toISOString(),
            meetingTypeIds: filters.meetingTypeIds,
            totalMeetingTypes: filters.totalMeetingTypes,
        }),
        [filters],
    );
    const { apComplianceByResponsible } = RCAAPComplianceEntity.GetAPComplianceByResponsible(argsFilters);

    const selectedComplianceTypeData = getComplianceByType(apComplianceByResponsible, filters.complianceType);

    const sortResponsibleData = useCallback((data: IAPComplianceByResponsible[], order: string) => {
        let sortedData: IAPComplianceByResponsible[] = [];
        const dataWithScores = data.map((responsible) => {
            return {
                ...responsible,
                score: getRCAAPComplianceScoreByResponsible(responsible),
            };
        });

        if (order === RCAAPComplianceByResponsibleFilterEnum.high_to_low) {
            sortedData = dataWithScores.sort((a, b) => b.score - a.score);
        }

        if (order === RCAAPComplianceByResponsibleFilterEnum.low_to_high) {
            sortedData = dataWithScores.sort((a, b) => a.score - b.score);
        }

        return sortedData;
    }, []);

    const sortingTypes = useCallback(
        (data: IAPComplianceByResponsible[], criteria: string) => {
            switch (criteria) {
                case RCAAPComplianceByResponsibleFilterEnum.a_to_z_asc:
                    return data.sort((a, b) => a.user.first_name.localeCompare(b.user.first_name));
                case RCAAPComplianceByResponsibleFilterEnum.a_to_z_desc:
                    return data.sort((a, b) => b.user.first_name.localeCompare(a.user.first_name));
                case RCAAPComplianceByResponsibleFilterEnum.low_to_high:
                    return sortResponsibleData(data, RCAAPComplianceByResponsibleFilterEnum.low_to_high);
                case RCAAPComplianceByResponsibleFilterEnum.high_to_low:
                    return sortResponsibleData(data, RCAAPComplianceByResponsibleFilterEnum.high_to_low);
                default:
                    return data.sort((a, b) => a.user.first_name.localeCompare(b.user.first_name));
            }
        },
        [sortResponsibleData],
    );

    const barsData = useMemo(() => {
        const sortedData = sortingTypes(selectedComplianceTypeData as IAPComplianceByResponsible[], filters.typeOption);
        return sortedData.map((ap) => [
            {
                responsible: {
                    name: `${ap.user.first_name} ${ap.user.last_name}`,
                    id: ap.user.user_id,
                    media: ap.user.media?.media_url,
                },
                completedOnTimeAmount: ap.completed_on_time,
                pendingAmount: ap.pending,
                completedLateAmount: ap.completed_late,
                pendingLateAmount: ap.pending_late,
                total: ap.total_action_plans_by_responsible,
            },
        ]);
    }, [selectedComplianceTypeData, filters.typeOption, sortingTypes]);

    return (
        <div className={classes.card}>
            <div className={classes.cardHeader}>
                <div className={classes.cartTitleContainer}>
                    <Typography variant="rcaTitle" className={classes.cardTitle}>
                        {t("rca.compliance.ap_compliance.by_responsible.title")}
                    </Typography>
                    <IconButton onClick={() => handleSetComplianceType(ComplianceTypes.NONE)}>
                        <RefreshIcon />
                    </IconButton>
                </div>
                <RCAAPComplianceByResponsibleFilter filters={filters} setFilters={handleSetTypeFilter} />
            </div>
            <div className={classes.cardBody}>
                {barsData.length === 0 ? (
                    <ComplianceBar />
                ) : (
                    <>
                        <ComplianceBarPercentageReference />
                        {barsData
                            .filter((barData) =>
                                Object.keys(barData[0]).some(
                                    (key) =>
                                        (Object.values(IRCAAPBarComplianceTypes) as string[]).includes(key) &&
                                        barData[0][key as IRCAAPBarComplianceTypes] > 0,
                                ),
                            )
                            .map((barData, index) => (
                                <ComplianceBar data={barData} key={index} withBottomBorder={true} />
                            ))}
                        {barsData.length > 0 && <ComplianceBarReferences />}
                    </>
                )}
            </div>
        </div>
    );
};

export default APComplianceByResponsible;
